import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Grid,
  Divider,
} from "@mui/material";
import { Button, Snackbar, Alert as MuiAlert } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import DataObjectIcon from "@mui/icons-material/DataObject";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import dataService, { DataElementSummary } from "../services/data-service";
import { personalDataService } from "../services/personal-data-service";
import { RequestDialog } from "../components/RequestDialog";
import { config } from "../config/config";

type SortOption = "recent" | "name" | "requests";

interface FiltersState {
  search: string;
  identifiersOnly: boolean;
  openRequestsOnly: boolean;
  sortBy: SortOption;
}

const defaultFilters: FiltersState = {
  search: "",
  identifiersOnly: false,
  openRequestsOnly: false,
  sortBy: "recent",
};

type ResolvedStatus = "idle" | "loading" | "success" | "error";

interface ResolvedValueInfo {
  status: ResolvedStatus;
  value: string | null;
  error?: string;
}

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const highlightValue = (value: string | null): string => {
  if (value === null || value === undefined) {
    return "Not Available";
  }
  if (typeof value === "string" && value.trim().length === 0) {
    return "Empty";
  }
  return value;
};

const DataPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DataElementSummary[]>([]);
  const [filters, setFilters] = useState<FiltersState>(defaultFilters);
  const [resolvedValues, setResolvedValues] = useState<Record<number, ResolvedValueInfo>>({});
  const fetchTokenRef = useRef(0);
  const [requestOpen, setRequestOpen] = useState<boolean>(false);
  const [requestSubmitting, setRequestSubmitting] = useState<boolean>(false);
  const [requestTarget, setRequestTarget] = useState<DataElementSummary | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const buildResolvedMap = useCallback((items: DataElementSummary[]) => {
    const map: Record<number, ResolvedValueInfo> = {};
    items.forEach((item) => {
      if (item.latestValue) {
        map[item.dataElementId] = {
          status: "success",
          value: item.latestValue,
        };
      } else if (item.latestVaultId && item.latestConsentHeaderId) {
        // Vault-protected: set to idle (not loading) - user must manually reveal
        map[item.dataElementId] = {
          status: "idle",
          value: null,
        };
      } else {
        map[item.dataElementId] = {
          status: "success",
          value: item.latestValue ?? null,
        };
      }
    });
    return map;
  }, []);

  const handleRevealValue = useCallback(async (item: DataElementSummary) => {
    if (!item.latestVaultId || !item.latestConsentHeaderId) return;

    // Set loading state
    setResolvedValues((prev) => ({
      ...prev,
      [item.dataElementId]: {
        status: "loading",
        value: null,
      },
    }));

    try {
      const response = await personalDataService.getPersonalDataValues(item.latestConsentHeaderId, [item.latestVaultId.toString()]);
      let value: string | null = null;
      if (response.success) {
        const dataItem = personalDataService.getVaultPersonalData(response, item.latestVaultId.toString());
        value = dataItem?.originalValue ?? null;
      }

      setResolvedValues((prev) => ({
        ...prev,
        [item.dataElementId]: {
          status: value ? "success" : "error",
          value,
          error: value ? undefined : "Protected",
        },
      }));

      // Update summary if value was successfully retrieved
      if (value) {
        setSummary((prev) => prev.map((entry) => (entry.dataElementId === item.dataElementId ? { ...entry, latestValue: value } : entry)));
      }
    } catch (err) {
      setResolvedValues((prev) => ({
        ...prev,
        [item.dataElementId]: {
          status: "error",
          value: prev[item.dataElementId]?.value ?? null,
          error: "Protected",
        },
      }));
    }
  }, []);

  const loadData = useCallback(async () => {
    const fetchId = ++fetchTokenRef.current;
    try {
      setLoading(true);
      setError(null);
      const data = await dataService.getDataElementsSummary();
      if (fetchTokenRef.current !== fetchId) {
        return;
      }
      setSummary(data);
      const resolvedMap = buildResolvedMap(data);
      setResolvedValues(resolvedMap);
      // No automatic fetching - user must manually reveal vault-protected values
    } catch (err: any) {
      setError(err?.message || "Failed to load data elements");
    } finally {
      if (fetchTokenRef.current === fetchId) {
        setLoading(false);
      }
    }
  }, [buildResolvedMap]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, search: event.target.value }));
  };

  const handleToggleIdentifiers = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFilters((prev) => ({ ...prev, identifiersOnly: checked }));
  };

  const handleToggleOpenRequests = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFilters((prev) => ({ ...prev, openRequestsOnly: checked }));
  };

  const handleSortChange = (event: SelectChangeEvent<SortOption>) => {
    setFilters((prev) => ({ ...prev, sortBy: event.target.value as SortOption }));
  };

  const filteredSummary = useMemo(() => {
    let result = [...summary];

    if (filters.search.trim()) {
      const query = filters.search.trim().toLowerCase();
      result = result.filter((item) => item.elementName.toLowerCase().includes(query) || item.dataElementKey.toLowerCase().includes(query));
    }

    if (filters.identifiersOnly) {
      result = result.filter((item) => item.isIdentifier);
    }

    if (filters.openRequestsOnly) {
      result = result.filter((item) => item.openRequests > 0);
    }

    switch (filters.sortBy) {
      case "name":
        result.sort((a, b) => a.elementName.localeCompare(b.elementName));
        break;
      case "requests":
        result.sort((a, b) => b.openRequests - a.openRequests || b.totalRequests - a.totalRequests);
        break;
      case "recent":
      default:
        result.sort((a, b) => {
          const aDate = a.latestCollectionDate ? new Date(a.latestCollectionDate).getTime() : 0;
          const bDate = b.latestCollectionDate ? new Date(b.latestCollectionDate).getTime() : 0;
          return bDate - aDate;
        });
        break;
    }

    return result;
  }, [summary, filters]);

  const handleCardClick = (elementId: number) => {
    navigate(`/data/${elementId}`);
  };

  const renderFilters = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: { xs: "stretch", md: "center" },
      }}
    >
      <TextField
        fullWidth
        placeholder="Search data elements"
        value={filters.search}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: filters.search ? (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={() => setFilters((prev) => ({ ...prev, search: "" }))}>
                <Tooltip title="Clear search">
                  <FilterListIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={<Switch checked={filters.identifiersOnly} onChange={handleToggleIdentifiers} size="small" />}
          label="Identifiers"
        />
        <FormControlLabel
          control={<Switch checked={filters.openRequestsOnly} onChange={handleToggleOpenRequests} size="small" />}
          label="Open Requests"
        />
        <Select size="small" value={filters.sortBy} onChange={handleSortChange}>
          <MenuItem value="recent">Recent Activity</MenuItem>
          <MenuItem value="name">Name (A-Z)</MenuItem>
          <MenuItem value="requests">Open Requests</MenuItem>
        </Select>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );

  const getValueDisplay = (item: DataElementSummary) => {
    const resolved = resolvedValues[item.dataElementId];

    // Fallback case: if resolved state doesn't exist, determine display based on item properties
    if (!resolved) {
      if (item.latestValue) {
        return { text: highlightValue(item.latestValue), highlight: false, subdued: false };
      }
      // If vault-protected, show "Protected" instead of "Not Available"
      if (item.latestVaultId && item.latestConsentHeaderId) {
        return { text: "Protected", highlight: false, subdued: true };
      }
      return { text: "Not Available", highlight: false, subdued: true };
    }

    switch (resolved.status) {
      case "loading":
        return { text: "Decrypting…", highlight: false, subdued: false };
      case "success": {
        const displayValue = resolved.value ?? item.latestValue;
        return {
          text: highlightValue(displayValue ?? null),
          highlight: Boolean(resolved.value),
          subdued: !resolved.value,
        };
      }
      case "error":
        return { text: resolved.error || "Protected", highlight: false, subdued: true };
      case "idle":
        // Vault-protected but not yet revealed
        return { text: "Protected", highlight: false, subdued: true };
      default:
        // Default case: check if vault-protected
        if (item.latestVaultId && item.latestConsentHeaderId && !item.latestValue) {
          return { text: "Protected", highlight: false, subdued: true };
        }
        return { text: highlightValue(item.latestValue), highlight: false, subdued: !item.latestValue };
    }
  };

  const renderCard = (item: DataElementSummary) => {
    const valueDisplay = getValueDisplay(item);
    const resolved = resolvedValues[item.dataElementId];
    const showSpinner = resolved?.status === "loading";

    return (
      <Grid item key={item.dataElementId} xs={12} sm={6} lg={4}>
        <Card
          onClick={() => handleCardClick(item.dataElementId)}
          sx={{
            height: "100%",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : theme.palette.background.paper,
            cursor: "pointer",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              boxShadow: theme.shadows[6],
              transform: "translateY(-2px)",
            },
          }}
        >
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <DataObjectIcon color="primary" sx={{ fontSize: 24 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.elementName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Key: {item.dataElementKey}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                {item.isIdentifier && <Chip icon={<VerifiedUserIcon sx={{ fontSize: 16 }} />} label="Identifier" size="small" color="primary" />}
                {item.openRequests > 0 && (
                  <Chip icon={<WarningAmberIcon sx={{ fontSize: 16 }} />} label={`${item.openRequests} Open`} size="small" color="warning" />
                )}
              </Stack>
            </Stack>

            {item.elementDescription && (
              <Typography variant="body2" color="text.secondary">
                {item.elementDescription}
              </Typography>
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: valueDisplay.highlight
                  ? theme.palette.mode === "dark"
                    ? "rgba(76, 175, 80, 0.18)"
                    : "rgba(76, 175, 80, 0.12)"
                  : theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Latest Value
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    fontWeight: valueDisplay.highlight ? 600 : 500,
                    color: valueDisplay.highlight
                      ? theme.palette.success.main
                      : valueDisplay.subdued
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  }}
                >
                  {valueDisplay.text}
                </Typography>
                {resolved?.status === "idle" && item.latestVaultId && item.latestConsentHeaderId && (
                  <Tooltip title="Reveal protected value">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRevealValue(item);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {showSpinner && <CircularProgress size={16} />}
                {resolved?.status === "success" && resolved.value && (
                  <Tooltip title="Value revealed">
                    <VisibilityOffIcon fontSize="small" color="success" />
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Divider flexItem />

            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Collected
                </Typography>
                <Typography variant="body2">{formatDate(item.latestCollectionDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Collection Points
                </Typography>
                <Typography variant="body2">{item.collectionPointCount}</Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total Requests
                </Typography>
                <Typography variant="body2">{item.totalRequests}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Open Requests
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: item.openRequests > 0 ? theme.palette.warning.main : theme.palette.text.primary,
                    fontWeight: item.openRequests > 0 ? 600 : 400,
                  }}
                >
                  {item.openRequests}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  setRequestTarget(item);
                  setRequestOpen(true);
                }}
                aria-label={`Create new request for ${item.elementName}`}
                disabled={requestSubmitting}
              >
                New Request
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Data Elements
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review the personal data collected across your consent interactions. Filter by identifiers, activity, or open requests to focus on what
          matters and drill into details.
        </Typography>
      </Box>

      {renderFilters()}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredSummary.length === 0 ? (
        <Box
          sx={{
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          }}
        >
          <Typography variant="h6">No data elements match your filters</Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting the filters or refresh to load the latest information.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredSummary.map(renderCard)}
        </Grid>
      )}
      <RequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        elementName={requestTarget?.elementName || ""}
        elementKey={requestTarget?.dataElementKey || ""}
        submitting={requestSubmitting}
        onSubmit={async ({ requestType, description, reason }) => {
          if (!requestTarget) return;
          try {
            setRequestSubmitting(true);
            await dataService.createDataElementRequest({
              requestType,
              description,
              reason,
              dataElementId: requestTarget.dataElementId,
              dataElementKey: requestTarget.dataElementKey,
            });
            // Optimistic update: increment counts on the target card
            setSummary((prev) =>
              prev.map((el) =>
                el.dataElementId === requestTarget.dataElementId
                  ? { ...el, totalRequests: el.totalRequests + 1, openRequests: el.openRequests + 1 }
                  : el
              )
            );
            setSnackbar({ open: true, message: "Request submitted successfully.", severity: "success" });
            setRequestOpen(false);
          } catch (err: any) {
            setSnackbar({ open: true, message: err?.message || "Failed to submit request.", severity: "error" });
          } finally {
            setRequestSubmitting(false);
          }
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={config.ui.snackbar.autoHideDuration}
        anchorOrigin={config.ui.snackbar.anchorOrigin}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <MuiAlert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" elevation={6}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default DataPage;
